import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { Mail, X, Save, Eye, EyeOff, Plus, Minus } from 'lucide-react';

const EmailConfigModal = ({ eventId, eventName, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
    host: 'smtp.gmail.com',
    smtpUsername: '',
    cc: [],
    bcc: []
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  // Fetch existing email configuration
  const { data: emailConfig, isLoading: configLoading } = useQuery({
    queryKey: ['eventEmailConfig', eventId],
    queryFn: () => ApiService.getEventEmailConfig(eventId),
    enabled: !!eventId,
  });

  // Update form data when config is loaded
  useEffect(() => {
    if (emailConfig) {
      setFormData({
        email: emailConfig.email || '',
        password: '', // Always start with empty password for security
        displayName: emailConfig.displayName || '',
        host: emailConfig.host || 'smtp.gmail.com',
        smtpUsername: emailConfig.smtpUsername || '',
        cc: emailConfig.cc || [],
        bcc: emailConfig.bcc || []
      });
    }
  }, [emailConfig]);

  // Save email configuration mutation
  const saveEmailConfigMutation = useMutation({
    mutationFn: (configData) => ApiService.updateEventEmailConfig(eventId, configData),
    onSuccess: () => {
      queryClient.invalidateQueries(['eventEmailConfig', eventId]);
      showSuccess('Email configuration saved successfully!');
      onClose();
    },
    onError: (error) => {
      showError('Failed to save email configuration. Please try again.');
      console.error('Save email config error:', error);
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addEmailToList = (listType) => {
    setFormData(prev => ({
      ...prev,
      [listType]: [...prev[listType], '']
    }));
  };

  const removeEmailFromList = (listType, index) => {
    setFormData(prev => ({
      ...prev,
      [listType]: prev[listType].filter((_, i) => i !== index)
    }));
  };

  const updateEmailInList = (listType, index, email) => {
    setFormData(prev => ({
      ...prev,
      [listType]: prev[listType].map((item, i) => i === index ? email : item)
    }));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSave = () => {
    if (!formData.email.trim()) {
      showError('Email address is required');
      return;
    }

    if (!validateEmail(formData.email)) {
      showError('Please enter a valid email address');
      return;
    }

    if (!formData.displayName.trim()) {
      showError('Display name is required');
      return;
    }

    // Validate CC emails
    const invalidCC = formData.cc.filter(email => email.trim() && !validateEmail(email.trim()));
    if (invalidCC.length > 0) {
      showError('Please enter valid CC email addresses');
      return;
    }

    // Validate BCC emails
    const invalidBCC = formData.bcc.filter(email => email.trim() && !validateEmail(email.trim()));
    if (invalidBCC.length > 0) {
      showError('Please enter valid BCC email addresses');
      return;
    }

    // Only require password if no existing config or if isPasswordAdded is false
    const isNewConfig = !emailConfig || !emailConfig.email;
    const needsPassword = isNewConfig || !emailConfig.passwordAdded;
    
    if (needsPassword && !formData.password.trim()) {
      showError('Password is required');
      return;
    }

    // Prepare config data with new structure
    const configData = {
      email: formData.email.trim(),
      displayName: formData.displayName.trim(),
      host: formData.host.trim() || 'smtp.gmail.com',
      smtpUsername: formData.smtpUsername.trim() || formData.email.trim(), // Default to email if not provided
      cc: formData.cc.filter(email => email.trim()).map(email => email.trim()),
      bcc: formData.bcc.filter(email => email.trim()).map(email => email.trim()),
      isPasswordAdded: !!formData.password.trim() || emailConfig?.isPasswordAdded || false
    };

    // Only include password if it's provided
    if (formData.password.trim()) {
      configData.password = formData.password;
    }

    saveEmailConfigMutation.mutate(configData);
  };

  const handleCancel = () => {
    onClose();
  };

  // Handle ESC key to close modal
  const handleKeyPress = useCallback((event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Handle click outside modal to close
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  // Add/remove event listener for ESC key
  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  const isConfigured = emailConfig && emailConfig.email && emailConfig.displayName;
  const isNewConfig = !emailConfig || !emailConfig.email;
  const needsPassword = isNewConfig || !emailConfig?.isPasswordAdded;

  if (configLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Loading email configuration...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <Card 
          className="w-full max-w-lg max-h-[90vh] flex flex-col my-8" 
          onClick={(e) => e.stopPropagation()}
        >
          <CardHeader className="border-b flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Mail className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-xl">
                  Email Configuration - {eventName}
                </CardTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-4 overflow-y-auto flex-1">
          <div className="space-y-4">
            <div>
              <Label htmlFor="smtp-email">Email Address *</Label>
              <Input
                id="smtp-email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="your-email@gmail.com"
                className="mt-1"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                The email address that will send notifications
              </p>
            </div>

            <div>
              <Label htmlFor="smtp-password">
                {needsPassword ? 'Password/App Password *' : 'Update Password (Optional)'}
              </Label>
              <div className="relative mt-1">
                <Input
                  id="smtp-password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder={needsPassword ? "Enter your email password or app password" : "Enter new password to update"}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {needsPassword 
                  ? "For Gmail, use an App Password instead of your regular password"
                  : "Leave empty to keep existing password"
                }
              </p>
            </div>

            <div>
              <Label htmlFor="smtp-displayName">Display Name *</Label>
              <Input
                id="smtp-displayName"
                type="text"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                placeholder="Event Notification"
                className="mt-1"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                The name that will appear as the sender
              </p>
            </div>

            <div>
              <Label htmlFor="smtp-host">SMTP Host</Label>
              <Input
                id="smtp-host"
                type="text"
                value={formData.host}
                onChange={(e) => handleInputChange('host', e.target.value)}
                placeholder="smtp.gmail.com"
                className="mt-1"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to use default (smtp.gmail.com)
              </p>
            </div>

            <div>
              <Label htmlFor="smtp-username">SMTP Username (Optional)</Label>
              <Input
                id="smtp-username"
                type="text"
                value={formData.smtpUsername}
                onChange={(e) => handleInputChange('smtpUsername', e.target.value)}
                placeholder="Usually same as email address"
                className="mt-1"
                autoComplete="off"
              />
              <p className="text-xs text-gray-500 mt-1">
                If different from email address, specify here
              </p>
            </div>

            {/* CC Recipients */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>CC Recipients (Optional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addEmailToList('cc')}
                  className="h-8"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add CC
                </Button>
              </div>
              {formData.cc.map((email, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => updateEmailInList('cc', index, e.target.value)}
                    placeholder="cc-recipient@example.com"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeEmailFromList('cc', index)}
                    className="h-10 w-10 p-0"
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              {formData.cc.length === 0 && (
                <p className="text-xs text-gray-500">No CC recipients added</p>
              )}
            </div>

            {/* BCC Recipients */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>BCC Recipients (Optional)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addEmailToList('bcc')}
                  className="h-8"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add BCC
                </Button>
              </div>
              {formData.bcc.map((email, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => updateEmailInList('bcc', index, e.target.value)}
                    placeholder="bcc-recipient@example.com"
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeEmailFromList('bcc', index)}
                    className="h-10 w-10 p-0"
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              {formData.bcc.length === 0 && (
                <p className="text-xs text-gray-500">No BCC recipients added</p>
              )}
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Gmail Setup Instructions:</h4>
            <ol className="text-sm text-blue-800 space-y-1">
              <li>1. Enable 2-Factor Authentication on your Gmail account</li>
              <li>2. Generate an App Password: Google Account → Security → App passwords</li>
              <li>3. Use the App Password (not your regular password) in the password field</li>
              <li>4. The system will use smtp.gmail.com as the default host</li>
            </ol>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saveEmailConfigMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saveEmailConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailConfigModal;
